import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

/**
 * Consolidated warning shown when the franchise tries to re-order one or
 * more SKUs that already have open pending procurement. WARNING-ONLY —
 * the YES button must always be enabled. We never block the order; the
 * spec is explicit that the franchise keeps final say.
 *
 * Props:
 *   open         : bool                — show/hide
 *   matches      : [{order_no, order_id, order_date, order_status,
 *                    line_status, product_id, product_name, product_sku,
 *                    pending_qty}]
 *   periodDays   : int                 — surfaces the configured window
 *   onConfirm    : () => void          — YES handler (proceed with order)
 *   onCancel     : () => void          — NO handler (stay on the page)
 *   testIdPrefix : str (default 'dup') — for e2e selectors
 */
export default function DuplicatePendingModal({
  open, matches = [], periodDays, onConfirm, onCancel,
  testIdPrefix = 'dup',
}) {
  if (!open) return null;
  // Roll the per-line rows up to "per product" for the headline summary.
  // The detail table below still shows every row.
  const byProduct = {};
  for (const m of matches) {
    const k = m.product_id;
    byProduct[k] = byProduct[k] || {
      name: m.product_name, sku: m.product_sku, qty: 0, lines: 0,
    };
    byProduct[k].qty += Number(m.pending_qty || 0);
    byProduct[k].lines += 1;
  }
  const productSummary = Object.values(byProduct);
  const isSingle = productSummary.length === 1;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[80]"
      data-testid={`${testIdPrefix}-modal`}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <h2 className="text-lg font-bold text-amber-900">
              {isSingle ? 'Product Already Pending Procurement' : 'Products Already Pending Procurement'}
            </h2>
            <p className="text-xs text-amber-700 mt-0.5">
              {periodDays
                ? `Found in your open orders from the last ${periodDays} day${periodDays === 1 ? '' : 's'}.`
                : 'Found in your open orders.'}
            </p>
          </div>
          <button onClick={onCancel} className="text-amber-700 hover:text-amber-900"
            data-testid={`${testIdPrefix}-modal-close`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {/* Headline rollup */}
          <p className="text-sm text-slate-700 mb-3">
            The following {isSingle ? 'product is' : `${productSummary.length} products are`} already
            pending procurement from {isSingle ? 'a previous order' : 'previous orders'}:
          </p>
          <div className="border rounded-lg overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left text-xs uppercase tracking-wide">Product</th>
                  <th className="px-3 py-2 text-right text-xs uppercase tracking-wide">Pending Qty</th>
                </tr>
              </thead>
              <tbody>
                {productSummary.map((p) => (
                  <tr key={p.sku || p.name} className="border-t">
                    <td className="px-3 py-2">
                      <p className="font-medium">{p.name}</p>
                      {p.sku && <p className="text-xs text-slate-500 font-mono">{p.sku}</p>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-amber-700">
                      {p.qty.toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Per-order detail rows */}
          <details className="text-xs text-slate-600 mb-2">
            <summary className="cursor-pointer hover:text-slate-900">
              Show full order details ({matches.length} line{matches.length === 1 ? '' : 's'})
            </summary>
            <div className="mt-2 border rounded overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Order</th>
                    <th className="px-2 py-1.5 text-left">Date</th>
                    <th className="px-2 py-1.5 text-left">Product</th>
                    <th className="px-2 py-1.5 text-right">Qty</th>
                    <th className="px-2 py-1.5 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((m) => (
                    <tr key={`${m.order_id}-${m.product_id}`} className="border-t"
                      data-testid={`${testIdPrefix}-row-${m.order_no}-${m.product_id}`}>
                      <td className="px-2 py-1.5 font-mono text-blue-700">{m.order_no}</td>
                      <td className="px-2 py-1.5 text-slate-500">
                        {m.order_date ? new Date(m.order_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-2 py-1.5">{m.product_name}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{Number(m.pending_qty).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-slate-600">
                        {(m.line_status || m.order_status || '').replace(/_/g, ' ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          <p className="text-sm font-medium text-slate-800 mt-4">
            Do you want to place an additional order anyway?
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Tip: doubling up creates additional procurement demand.
            Choose <span className="font-semibold">No</span> if you&apos;d rather wait for the existing order to be fulfilled.
          </p>
        </div>

        {/* Footer */}
        <div className="border-t bg-slate-50 px-6 py-3 flex items-center justify-end gap-2">
          <button onClick={onCancel}
            className="px-4 py-2 rounded border border-slate-300 hover:bg-white text-sm font-medium"
            data-testid={`${testIdPrefix}-cancel-btn`}>
            No — Cancel
          </button>
          <button onClick={onConfirm}
            className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium"
            data-testid={`${testIdPrefix}-confirm-btn`}>
            Yes — Place Order Anyway
          </button>
        </div>
      </div>
    </div>
  );
}
